import { computed, ref } from 'vue';

/**
 * Thin orchestrator for the multi-phase WizardDialog. Keeps the active phase
 * as state and exposes type-safe transitions (next / back / goTo / reset).
 *
 * Usage:
 *   const phases = ['select', 'credits', 'review'] as const;
 *   const wizard = useWizard(phases);
 *
 *   <WizardDialog v-model:phase="wizard.phase.value" :phases="phases" ... />
 */
export function useWizard<T extends string>(phases: readonly T[]) {
  if (phases.length === 0) {
    throw new Error('useWizard: at least one phase is required');
  }

  const phase = ref<T>(phases[0]) as { value: T };

  const index = computed(() => phases.indexOf(phase.value));
  const isFirst = computed(() => index.value === 0);
  const isLast = computed(() => index.value === phases.length - 1);

  const next = () => {
    const i = index.value;
    if (i < phases.length - 1) {
      phase.value = phases[i + 1];
    }
  };

  const back = () => {
    const i = index.value;
    if (i > 0) {
      phase.value = phases[i - 1];
    }
  };

  const goTo = (target: T) => {
    if (!phases.includes(target)) {
      // eslint-disable-next-line no-console
      console.warn(`[useWizard] unknown phase: ${target}`);
      return;
    }
    phase.value = target;
  };

  const reset = () => {
    phase.value = phases[0];
  };

  return {
    phase,
    index,
    isFirst,
    isLast,
    next,
    back,
    goTo,
    reset,
  };
}
